/**
 * Salle de partage : `/t/<identifiant>#k=<clé>`.
 *
 * ## Comment le rôle est déterminé
 *
 * Il n'y a **pas** de paramètre de rôle dans l'URL — ce serait un aveu inutile au serveur
 * et une source d'erreur. La règle est locale à l'onglet :
 *
 * - les fichiers sont encore en mémoire pour cette salle → on est **émetteur** ;
 * - sinon → on est **récepteur**.
 *
 * Le cas limite est le rechargement de page côté émetteur : le navigateur efface la
 * référence aux fichiers (garantie de sécurité). Un marqueur en `sessionStorage` permet
 * alors de reconnaître la situation et de l'expliquer, au lieu de laisser croire à
 * l'utilisateur qu'il est devenu destinataire de son propre partage.
 */

import { useEffect, useMemo, useRef } from 'react';
import { parseKeyFromFragment, type Role } from '@attrape/shared';
import { useFormatters, useT } from '../i18n/index.js';
import { navigate } from '../router.js';
import {
  acquireSession,
  getStagedFiles,
  releaseSession,
  wasSender,
  type ShareSession,
} from '../session.js';
import { useAppStore } from '../store.js';
import { ErrorAlert, SuccessAlert, WarningAlert } from '../ui/Alerts.js';
import { IncomingRequest } from '../ui/IncomingRequest.js';
import { ProgressCard } from '../ui/ProgressCard.js';
import { ShareLink } from '../ui/ShareLink.js';
import { StatusBar } from '../ui/StatusBar.js';

interface RoomProps {
  roomId: string;
}

export function Room({ roomId }: RoomProps): React.JSX.Element {
  const translator = useT();
  const format = useFormatters();

  // Le fragment est lu une seule fois : il ne change pas pendant la session.
  const key = useMemo(() => parseKeyFromFragment(window.location.hash), []);
  const stagedFiles = useMemo(() => getStagedFiles(roomId), [roomId]);
  const senderForgotFiles = useMemo(() => wasSender(roomId), [roomId]);

  const phase = useAppStore((state) => state.phase);
  const error = useAppStore((state) => state.error);
  const warning = useAppStore((state) => state.warning);
  const shareUrl = useAppStore((state) => state.shareUrl);
  const result = useAppStore((state) => state.result);
  const incoming = useAppStore((state) => state.incoming);

  const session = useRef<ShareSession | null>(null);

  const role: Role = stagedFiles ? 'sender' : 'receiver';
  const usable = key !== null && !(senderForgotFiles && !stagedFiles);

  useEffect(() => {
    if (!key || !usable) return;
    const active = acquireSession(roomId, key, role);
    session.current = active;
    return () => {
      session.current = null;
      releaseSession(active);
    };
  }, [roomId, key, role, usable]);

  const goHome = (): void => {
    navigate('/');
  };

  const cancel = (): void => {
    session.current?.dispose();
    session.current = null;
    goHome();
  };

  // ── Cas d'erreur précoces, avant toute connexion ──────────────────────────

  if (!key) {
    return (
      <ErrorAlert
        title={translator.t('room.errorTitle')}
        message={{ kind: 'key', key: 'room.errorNoKey' }}
        onRetry={goHome}
        retryLabel={translator.t('room.createNewShare')}
      />
    );
  }

  if (senderForgotFiles && !stagedFiles) {
    return (
      <ErrorAlert
        title={translator.t('room.errorTitle')}
        message={{ kind: 'key', key: 'room.errorForgotten' }}
        onRetry={goHome}
        retryLabel={translator.t('room.chooseFiles')}
      />
    );
  }

  // ── Rendu par phase ───────────────────────────────────────────────────────

  const receivedHeading =
    result?.allVerified === false
      ? translator.t('room.receivedUnverified')
      : translator.t('room.received');

  return (
    <div className="stack">
      <StatusBar />

      {warning && <WarningAlert message={warning} />}

      {phase === 'error' && error && (
        <ErrorAlert message={error} onRetry={goHome} retryLabel={translator.t('room.newShare')} />
      )}

      {phase === 'connecting' && (
        <div className="card">
          <h2>{translator.t('room.connecting')}</h2>
          <p className="muted">{translator.t('room.connectingHint')}</p>
        </div>
      )}

      {phase === 'waiting-peer' && (
        <>
          <div className="card">
            <h2>{translator.t('room.waitingPeer')}</h2>
            <p className="muted">{translator.t('room.waitingPeerHint')}</p>
            {shareUrl && <ShareLink url={shareUrl} />}
          </div>

          <div className="card">
            <h2>{translator.t('room.keepInMind')}</h2>
            <ul className="steps">
              <li>{translator.t('room.keep1')}</li>
              <li>{translator.t('room.keep2')}</li>
              <li>{translator.t('room.keep3')}</li>
            </ul>
            <p style={{ marginBottom: 0, marginTop: '1rem' }}>
              <button type="button" className="btn btn-sm" onClick={cancel}>
                {translator.t('room.cancelShare')}
              </button>
            </p>
          </div>
        </>
      )}

      {phase === 'negotiating' && (
        <div className="card">
          <h2>{translator.t('room.negotiating')}</h2>
          <p className="muted">{translator.t('room.negotiatingHint')}</p>
        </div>
      )}

      {/*
        Reconnexion : on dit explicitement que le travail déjà fait est conservé. Sans
        cette précision, l'utilisateur d'un transfert de 3 Go interrompu à 80 % croirait
        devoir tout recommencer — et fermerait l'onglet, perdant la reprise.
      */}
      {phase === 'reconnecting' && (
        <div className="card">
          <h2>{translator.t('room.reconnecting')}</h2>
          <p className="muted">{translator.t('room.reconnectingHint')}</p>
        </div>
      )}

      {phase === 'awaiting-decision' && incoming && (
        <IncomingRequest
          onAccept={() => {
            // Appel direct depuis le clic : le sélecteur de destination a besoin de
            // l'activation utilisateur, qui serait perdue après un `await`.
            void session.current?.acceptOffer();
          }}
          onReject={() => void session.current?.rejectOffer()}
        />
      )}

      {phase === 'transferring' && (
        <ProgressCard
          title={
            role === 'sender'
              ? translator.t('progress.sending')
              : translator.t('progress.receiving')
          }
          onCancel={cancel}
        />
      )}

      {phase === 'done' && (
        <div className="stack">
          <SuccessAlert title={role === 'sender' ? translator.t('room.sent') : receivedHeading}>
            {role === 'sender' ? (
              <p>{translator.t('room.sentBody')}</p>
            ) : result ? (
              <>
                <p>
                  {translator.plural('room.receivedBody', result.files.length)}{' '}
                  {translator.t('room.resultTotal', { size: format.bytes(result.totalBytes) })}
                </p>
                <ul className="file-list" style={{ marginTop: '0.75rem' }}>
                  {result.files.map((file) => (
                    <li className="file-item" key={file.index}>
                      <span className="file-name" title={file.name}>
                        {file.name}
                      </span>
                      <span className="file-size">
                        {file.verified
                          ? translator.t('room.verified')
                          : translator.t('room.unverified')}{' '}
                        · {format.bytes(file.bytes)}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </SuccessAlert>

          <p style={{ margin: 0 }}>
            <button type="button" className="btn" onClick={cancel}>
              {translator.t('room.finish')}
            </button>
          </p>
        </div>
      )}

      {phase === 'idle' && (
        <div className="card">
          <h2>{translator.t('room.preparing')}</h2>
        </div>
      )}
    </div>
  );
}
